import { listItemSectionBuilder } from '@wix/design-system';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CollectionFilterState,
  DropdownCollectionFilterState,
} from '../../state';
import { CollectionState, FiltersMap, TextOverflowState } from '@wix/bex-core';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { observer } from 'mobx-react-lite';
import {
  KeyedItemOption,
  MultiSelectCheckboxFilterBase,
  MultiSelectCheckboxFilterBaseOption,
  MultiSelectCheckboxFilterBaseProps,
} from '../MultiSelectCheckboxFilterBase';
import { ListItem, useBuildOption } from '../../hooks';
import { InputOverflow } from '../InputOverflow';
import { useFilterToolbarState } from '../../providers';
import { usePlaceholderStateOptions } from '../../hooks/usePlaceholderStateOptions';
import {
  useCollectionFilterInitialFetchTiming,
  CollectionFilterInitialFetchTimingProps,
} from '../../hooks/useCollectionFilterInitialFetchTiming';

export interface MultiSelectCheckboxCollectionFilterProps<
  V,
  F extends FiltersMap = FiltersMap,
> extends Omit<
      MultiSelectCheckboxFilterBaseProps<V>,
      'options' | 'dropdownSelect'
    >,
    CollectionFilterInitialFetchTimingProps {
  /**
   * A collection state object to fetch the dropdown items.<br>
   * Typically, a collection created using [useFilterCollection](/pages/cairo/?path=/story/common-hooks--usefiltercollection) hook.
   */
  collection: CollectionState<V, F>;
  /**
   * A function that returns a [ListItemSelectProps](https://www.docs.wixdesignsystem.com/?path=/story/components-form-dropdownlistitems--listitemselect) (Optional)
   */
  renderItem?: (item: V, index: number) => ListItem;
}

function _MultiSelectCheckboxCollectionFilter<
  V,
  F extends FiltersMap = FiltersMap,
>(props: MultiSelectCheckboxCollectionFilterProps<V, F>) {
  const {
    filter,
    collection,
    renderItem,
    onSelect,
    onClose,
    onDeselect,
    placeholder: placeholderProp,
    ...rest
  } = props;

  const container = useWixPatternsContainer();
  const toolbar = useFilterToolbarState();

  const buildOption = useBuildOption<V>({
    filter,
    renderItem,
    checkbox: true,
  });

  const [collectionFilter] = useState(
    () =>
      new CollectionFilterState({
        collection,
        filter,
      }),
  );

  const [dropdownCollectionFilterState] = useState(
    () =>
      new DropdownCollectionFilterState<V, F>({
        resetOnClose: false,
        collectionFilter,
        container,
        toolbar,
      }),
  );

  const { dropdownSelect, init, inputState, shouldShowPlaceholder } =
    dropdownCollectionFilterState;
  const { disableEditing } = inputState;

  const {
    select: { selectedKeys, selectedValues },
    finalFilterKeyedItems,
    finalFilterKeyedItemsMap,
    container: { translate: t },
  } = dropdownSelect;

  const [textOverflowState] = useState(
    () =>
      new TextOverflowState({
        observable: () => dropdownSelect.select.selectedValues,
      }),
  );
  const { isOverflow } = textOverflowState;

  const {
    result: { keyedItems },
    fetchedLastPage,
    query: {
      search: { isEmpty: isEmptySearch },
    },
  } = collection;

  const promotedOptions: MultiSelectCheckboxFilterBaseOption<V>[] =
    finalFilterKeyedItems.length
      ? [
          ...finalFilterKeyedItems.map(buildOption),
          listItemSectionBuilder({
            id: 'promoted-section-divider',
            type: 'divider',
          }),
        ]
      : [];

  const otherOptions: KeyedItemOption<V>[] = (
    isEmptySearch
      ? keyedItems.filter(
          ({ key }) => !finalFilterKeyedItemsMap.has(key) ?? true,
        )
      : keyedItems
  ).map(buildOption);

  const { options, fixedFooter, showInitialLoader } =
    usePlaceholderStateOptions(
      [...(isEmptySearch ? promotedOptions : []), ...otherOptions],
      {
        collection,
      },
    );

  const selectedValuesText = useMemo(() => {
    return selectedValues.map(({ item }) => filter.itemName(item)).join(', ');
  }, [selectedValues]);

  const { skipInitialFetch, onFocus } =
    useCollectionFilterInitialFetchTiming(props);

  useEffect(() => init({ skipInitialFetch }), []);

  const placeholder = useMemo(() => {
    if (!shouldShowPlaceholder) {
      return '';
    }

    if (selectedValues.length !== 0) {
      if (
        (process.env.NODE_ENV === 'test' && selectedValuesText.length > 40) ||
        isOverflow
      ) {
        return t('cairo.dropdownSearch.filters.selected', {
          total: selectedValues.length,
        });
      }

      return selectedValuesText;
    }

    return (
      placeholderProp ?? t('cairo.dropdownSearch.select.filers.placeholder')
    );
  }, [
    selectedValues,
    selectedValuesText,
    shouldShowPlaceholder,
    isOverflow,
    placeholderProp,
  ]);

  return (
    <MultiSelectCheckboxFilterBase
      {...rest}
      fixedFooter={fixedFooter}
      infiniteScroll
      filter={filter}
      dropdownSelect={dropdownSelect}
      options={options}
      selectedOptions={selectedKeys}
      hasMore={!showInitialLoader && !fetchedLastPage}
      loadMore={collection.fetchNextPageIfNeeded}
      onClose={() => {
        if (!dropdownSelect.hasPendingFilter) {
          dropdownSelect.closeAndChangeSelectedItems();
        }
        inputState.onBlur();
        onClose?.();
      }}
      onSelect={(id, option) => {
        const { keyedItem } = option as KeyedItemOption<V>;
        dropdownSelect.select.set(keyedItem);
        if (dropdownSelect.hasPendingFilter) {
          dropdownSelect.refreshIfChanged({
            actionType: 'add',
            clickedValueKey: keyedItem.key,
          });
        }
        onSelect?.(id, option);
      }}
      onDeselect={(id, option) => {
        const { keyedItem } = option as KeyedItemOption<V>;
        dropdownSelect.select.delete(keyedItem);
        if (dropdownSelect.hasPendingFilter) {
          dropdownSelect.refreshIfChanged({
            actionType: 'remove',
            clickedValueKey: keyedItem.key,
          });
        }
        onDeselect?.(id, option);
      }}
      value={collection.query.search.value}
      onFocus={() => {
        inputState.onFocus();
        onFocus();
      }}
      onBlur={() => {
        inputState.onBlur();
      }}
      onChange={(e) => {
        collection.search(e.target.value, { clearResult: true });
      }}
      onOptionsShow={inputState.onOptionsShow}
      onKeyDown={(e) => {
        if (e.nativeEvent.code === 'Tab') {
          inputState.onTabKeyDown();
        }
      }}
      onClickOutside={(e) => {
        props.onClickOutside?.(e);
        inputState.onClickOutside();
      }}
      onClear={() => {
        inputState.onBlur();
        collection.clearSearch();
      }}
      disableEditing={disableEditing}
      placeholder={placeholder}
      clearButton={!disableEditing}
      clearButtonTooltipContent={t('cairo.dropdownSearch.select.filers.clear')}
      focusOnClearClick
      inputElement={
        <InputOverflow
          selected={selectedValues.length > 0}
          focused={inputState.isFocused}
          text={selectedValuesText}
          state={textOverflowState}
        />
      }
    />
  );
}

export const MultiSelectCheckboxCollectionFilter = observer(
  _MultiSelectCheckboxCollectionFilter,
);
